import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
} from '@angular/core';
import {
  iconBreakpoint,
  xxsmallBreakpoint,
} from '@epicuro-next/ataraxia/image';
import { dynamicElementsProductCardKey } from '@epicuro-next/constants/dynamic-elements';
import {
  ProductCardConfiguration,
  ProductCardConfigurationKeys,
  PRODUCT_CARD_FEATURE_NAME,
} from '@epicuro-next/constants/rule-engine/product-card-rules';
import {
  selectConfigurationValueFrom,
  selectProductImageMissingVisual,
} from '@epicuro-next/platform/configuration';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import {
  IDabEntry,
  IDabType,
  selectDABinBasket,
  selectDABQuantities,
} from '@epicuro-next/state/dab-state';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { selectIsProductMarketing } from '@epicuro-next/state/merchandise-state';
import { selectExpandingUnavailableProductCardEnabled } from '@epicuro-next/state/p-basket-state';
import { deleteProductFromOpenedList } from '@epicuro-next/state/product-list-state';
import {
  hasDiscountIndicator,
  isNonNullish,
  isNullish,
  stringValueToBoolean,
} from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import {
  MediaType,
  Product,
  ProductMedia,
} from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

import { ProductCardBaseComponent } from '../product-card-base/product-card-base.component';

import { fadeInOut, slideDown } from './dab-items-animations';

/**
 * Product card component
 */
@DynamicComponentRegister(dynamicElementsProductCardKey, true)
@Component({
  selector: 'epc-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInOut, slideDown],
  standalone: false,
})
export class ProductCardComponent
  extends ProductCardBaseComponent
  implements OnDestroy, OnChanges
{
  public readonly imageBreakpoints = [xxsmallBreakpoint];
  public readonly carouselBreakpoints = [iconBreakpoint];
  public dabItemsHovered$ = new BehaviorSubject(false);
  public readonly showSimilarProducts = signal(false);

  public extendedDabItemsEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ProductCardConfigurationKeys.ExtendedDabItems,
        ProductCardConfiguration
      >(
        PRODUCT_CARD_FEATURE_NAME,
        ProductCardConfigurationKeys.ExtendedDabItems,
        false,
      ),
    ),
  );

  public showImgOverflow$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ProductCardConfigurationKeys.ProductShowCarouselNumber,
        ProductCardConfiguration
      >(
        PRODUCT_CARD_FEATURE_NAME,
        ProductCardConfigurationKeys.ProductShowCarouselNumber,
        false,
      ),
    ),
  );

  public readonly expandingUnavailableProductCardEnabled$ = this.store.select(
    selectExpandingUnavailableProductCardEnabled,
  );

  public useThumbnailsSkeleton$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ProductCardConfigurationKeys.UseThumbnailsSkeleton,
        ProductCardConfiguration
      >(
        PRODUCT_CARD_FEATURE_NAME,
        ProductCardConfigurationKeys.UseThumbnailsSkeleton,
        true,
      ),
    ),
  );

  public showImgFullSpace$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        ProductCardConfigurationKeys.ProductShowMainImageFullSpace,
        ProductCardConfiguration
      >(
        PRODUCT_CARD_FEATURE_NAME,
        ProductCardConfigurationKeys.ProductShowMainImageFullSpace,
        true,
      ),
    ),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  public get discountVisible() {
    if (hasDiscountIndicator(this.product.labels)) {
      return false;
    }
    const productPrices = (this.prices || [])[0];

    if (isNonNullish(productPrices?.discount)) {
      this.discount = {
        max: productPrices.discount.max ?? 0,
        min: 0,
      };
    }

    return (this.discount?.max ?? 0) > 0;
  }

  /**
   * Method to get number of thumbnails to show in carousel
   */
  public get thumbnailsNumber(): number {
    if (this.breakpointObserver.isMatched('(min-width: 1200px)')) {
      return 4;
    }

    if (this.breakpointObserver.isMatched('(min-width: 768px)')) {
      return 3;
    }

    return 2;
  }

  /** Component destroy subject */
  private destroy$: Subject<void> = new Subject();

  @Input()
  public compactView = false;

  @Input()
  public set groupVariants(value: boolean) {
    this.groupVariants$.next(value);
  }

  @Input()
  public bookMarkNextAvailableVariant = false;

  @HostBinding('attr.data-e2e') get e2e() {
    return `p-card-id-${this.product?.productId}`;
  }

  public productMedia: ProductMedia[] = [];

  /**
   * Flag to show/hide the add to list toast.
   */
  public showToastMessage = false;

  /**
   * Data used to display DAB
   */
  public dabData$: Observable<IDabEntry[] | undefined>;

  public productImageMissingVisualConfig$ = this.store.select(
    selectProductImageMissingVisual,
  );

  /**
   * Data used to display DAB for in basket
   */
  public dabInBasketData$: Observable<IDabEntry | null>;
  public variantsMedia: ProductMedia[][];

  /**
   * Flag to show/hide the product detail
   * when mouse enter or leave
   */
  public showProductDetails = true;

  public groupVariants$ = new BehaviorSubject(false);

  /** Dependencies */
  constructor(
    private breakpointObserver: BreakpointObserver,
    store: Store,
    private cdr: ChangeDetectorRef,
  ) {
    super(store);
  }

  /**
   * Initializes and call scale images
   */
  public ngOnChanges(changes: SimpleChanges): void {
    combineLatest([
      this.showVariations$.pipe(map(Boolean)),
      this.groupVariants$,
    ])
      .pipe(
        filter(() => Boolean(this.product)),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe(([showVariations, groupVariants]) => {
        this.populateProductMedia(showVariations, groupVariants);
        this.cdr.detectChanges();
      });

    this.getDab();
    this.getDabInBasket();

    if (changes.product && changes.product.currentValue) {
      this.productSignal.set(changes.product.currentValue);
    }
  }

  public checkIfProductIsMarketing(productId: string) {
    return this.store.pipe(select(selectIsProductMarketing(productId)));
  }

  public updateCurrentVariant(index: number) {
    this.currentVariant.set(
      this.product.relatedVariants?.[index - 1] ?? this.product,
    );
    if (this.variantsMedia[index]) {
      this.currentVariantMedia = this.variantsMedia[index];
    }
  }

  public getProduct(index: number): Product {
    if (
      index === -1 ||
      isNullish(this.product.relatedVariants) ||
      this.product.relatedVariants.length === 0
    ) {
      return this.product;
    }
    const relatedVariantIndex = index - 1;
    const product =
      this.product.relatedVariants[relatedVariantIndex] ?? this.product;
    return product;
  }

  private getDab() {
    this.dabData$ = this.groupVariants$.pipe(
      filter(() => Boolean(this.product)),
      take(1),
      switchMap((collapse) => {
        if (!collapse || !this.product.relatedVariants) {
          return this.store.pipe(
            select(
              selectDABQuantities({
                productId: this.product.productId,
                brandId: this.product.brandId,
              }),
            ),
          );
        }

        return this.store.pipe(
          select(
            selectDABQuantities({
              productId: this.product.parentId ?? this.product.productId,
              brandId: this.product.brandId,
            }),
          ),
          map((dabs: IDabEntry[] | undefined) => {
            dabs?.reduce((acc, dab) => {
              const existingDabType = acc.find(({ type }) => type === dab.type);
              if (existingDabType) {
                existingDabType.quantity += dab.quantity;
              } else {
                acc.push(dab);
              }
              return acc;
            }, [] as IDabEntry[]);
            const dabDelivered = dabs?.find(
              ({ type }) => type === IDabType.DELIVERED,
            );
            const dabInbound = dabs?.find(
              ({ type }) => type === IDabType.INBOUND,
            );

            return [dabInbound, dabDelivered].filter(Boolean) as
              | IDabEntry[]
              | undefined;
          }),
        );
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getDabInBasket() {
    this.dabInBasketData$ = this.groupVariants$.pipe(
      filter(() => Boolean(this.product)),
      take(1),
      switchMap((collapse) => {
        if (!collapse || !this.product.relatedVariants) {
          return this.store.pipe(select(selectDABinBasket(this.product)));
        }

        return combineLatest([
          this.store.pipe(select(selectDABinBasket(this.product))),
          ...this.product.relatedVariants.map((variant) =>
            this.store.pipe(
              select(
                selectDABinBasket({
                  ...variant,
                  parent: this.product.parent,
                }),
              ),
            ),
          ),
        ]).pipe(
          map((dabs: (IDabEntry | null)[]) => {
            return dabs
              .filter(Boolean)
              .reduce((dab: IDabEntry, item: IDabEntry) => {
                if (!dab) {
                  return item;
                }

                return {
                  ...dab,
                  quantity: dab.quantity + item.quantity,
                };
              }, null);
          }),
        );
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Populate product media
   */
  private populateProductMedia(
    showVariations: boolean,
    groupVariants: boolean,
  ) {
    const queryByImageType = (media: ProductMedia) =>
      media.type.toLowerCase() === MediaType.Image.toLowerCase();

    const productMedia = this.product.media || [];
    this.variantsMedia = [this.product.media];
    this.currentVariant.set(this.product);
    this.currentVariantMedia =
      groupVariants && this.product.articleImageUrl
        ? [{ type: MediaType.Image, url: this.product.articleImageUrl }]
        : this.product.media;
    let resultMedia = [];

    if (showVariations && this.product.relatedVariants?.length) {
      const variationsMedias = this.product.relatedVariants.reduce(
        (media, variation) => {
          const variationMedia = variation.media.find(queryByImageType);
          return variationMedia ? [...media, variationMedia] : media;
        },
        [],
      );
      resultMedia = [productMedia[0], ...variationsMedias];
    } else {
      resultMedia = productMedia;
    }

    if (this.product.relatedVariants?.length) {
      this.variantsMedia = [
        ...this.variantsMedia,
        ...this.product.relatedVariants.map((variant) => variant.media),
      ];
    }

    this.productMedia = resultMedia
      .filter(isNonNullish)
      .filter(queryByImageType);
  }

  public deleteFromProductList(product: Product) {
    this.store.dispatch(
      deleteProductFromOpenedList({
        product,
      }),
    );
  }

  public getCarouselItems(shouldGroupVariants: boolean) {
    if (shouldGroupVariants && !this.product.relatedVariants?.length) {
      return [this.productMedia[0]];
    }

    return this.productMedia?.length > 1
      ? this.productMedia
      : this.currentVariantMedia;
  }

  public getCarouselToolTip(shouldGroupVariants: boolean) {
    let itemsCount;
    if (!shouldGroupVariants && !this.product.relatedVariants?.length) {
      itemsCount = (this.productMedia?.length ?? 0) - this.thumbnailsNumber;
      return $localize`:PCard|more color images available tooltip@@PCardMoreImages:${itemsCount} more images available`;
    }
    if (this.productMedia?.length > 1) {
      itemsCount = this.productMedia?.length - this.thumbnailsNumber;
      return $localize`:PCard|more variant images available tooltip@@PCardMoreColors:${itemsCount} more colors available`;
    }
    itemsCount = this.currentVariantMedia?.length - this.thumbnailsNumber;
    return $localize`:PCard|more color images available tooltip@@PCardMoreImages:${itemsCount} more images available`;
  }

  public trackDabItem(_: number, dabItem: IDabEntry) {
    return `${dabItem.type}${dabItem.quantity}`;
  }

  /** Angular's OnDestroy hook */
  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public isAttributeTrue(attribute?: string | boolean | (string | boolean)[]) {
    return stringValueToBoolean(
      Array.isArray(attribute) ? attribute?.[0] : attribute,
    );
  }

  public expandSimilarProducts() {
    this.showSimilarProducts.set(!this.showSimilarProducts());
  }
}
